/**
 * Reading the backend's error envelopes.
 *
 * The `api` client already turns a non-2xx into an `ApiError` carrying the
 * best single line it could find. What is here is the one case that needs
 * more than a line: a `validation_failed` 400, where the message itself says
 * only "Validation failed" and the useful part is a field at a time
 * underneath it.
 *
 * It lives in `lib/api` rather than in whichever feature first needed it,
 * because the envelope belongs to the API rather than to any one form — the
 * same reasoning that moved `PaginatedResponse` here.
 */

import { ApiError } from "../http";

/**
 * The envelope's own name for what went wrong — `validation_failed`,
 * `appointment_meeting_account_mismatch`, and so on. `undefined` for a body
 * that carries none, which is every error the backend has not given a code.
 *
 * Exported because a screen sometimes has something better to say about one
 * particular failure than the server's sentence — see the appointments
 * feature, which turns the meeting-account codes into a line naming the app
 * and the tab to fix it on.
 */
export const errorCode = (error: unknown): string | undefined => {
  if (!(error instanceof ApiError)) return undefined;

  const body = error.data;
  if (!body || typeof body !== "object") return undefined;

  const code = (body as { code?: unknown }).code;
  return typeof code === "string" && code.trim() ? code.trim() : undefined;
};

/**
 * The field-level problems out of a `validation_failed` body, as one line:
 * `email: Enter a valid email address. phone_number: …`.
 *
 * `undefined` when the body carries no `details` worth reading, which leaves
 * the caller with the `ApiError`'s own message.
 *
 * Only ever read for a `validation_failed`, which is the one code whose own
 * message — "Validation failed." — says nothing and whose `details` is a map
 * of field to problem. Every other code puts a written sentence in `message`
 * and uses `details` for context: an account mismatch sends
 * `{ provider: "GOOGLE_MEET" }` beside a message explaining that the meeting
 * belongs to another account. Flattening that produced the toast that read
 * "provider: GOOGLE_MEET" and threw the explanation away, which is the whole
 * reason the code is checked here rather than the shape alone.
 */
export const validationMessage = (error: ApiError): string | undefined => {
  if (errorCode(error) !== "validation_failed") return undefined;

  const body = error.data;
  if (!body || typeof body !== "object") return undefined;

  const details = (body as { details?: unknown }).details;
  if (!details || typeof details !== "object") return undefined;

  const problems = Object.entries(details as Record<string, unknown>)
    .map(([field, value]) => {
      const text = Array.isArray(value) ? value[0] : value;
      return typeof text === "string" && text.trim()
        ? `${field}: ${text.trim()}`
        : "";
    })
    .filter(Boolean);

  return problems.length > 0 ? problems.join(" ") : undefined;
};

/**
 * The same, already folded back into an `ApiError` — what a service throws on
 * when it wants the fields named rather than "Validation failed". An error
 * carrying no field detail is handed back untouched.
 */
export const withValidationDetail = (error: unknown): unknown => {
  if (!(error instanceof ApiError)) return error;

  const detailed = validationMessage(error);
  return detailed
    ? new ApiError(detailed, error.status, error.data)
    : error;
};
